import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  FileText,
  Download,
  CheckSquare,
  Square,
  Loader2,
  FileSpreadsheet,
  AlertCircle,
  CheckCircle
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ReportGenerator = ({ project }) => {
  const [availablePhases, setAvailablePhases] = useState([]);
  const [selectedPhases, setSelectedPhases] = useState([]);
  const [reportFormat, setReportFormat] = useState('pdf');
  const [includeCharts, setIncludeCharts] = useState(true);
  const [includeRawData, setIncludeRawData] = useState(false);
  const [reportTitle, setReportTitle] = useState('');
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [generationComplete, setGenerationComplete] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchAvailablePhases();
  }, [project.id]);

  const fetchAvailablePhases = async () => {
    try {
      const response = await axios.get(`${API}/reports/available-phases/${project.id}`);
      setAvailablePhases(response.data.available_phases);
      // Auto-select all available phases
      setSelectedPhases(response.data.available_phases.map(p => p.id));
    } catch (error) {
      console.error('Error fetching available phases:', error);
      setError('Failed to load available phases');
    } finally {
      setLoading(false);
    }
  };

  const togglePhase = (phaseId) => {
    setSelectedPhases(prev => 
      prev.includes(phaseId) 
        ? prev.filter(id => id !== phaseId)
        : [...prev, phaseId]
    );
  };

  const generateReport = async () => {
    if (selectedPhases.length === 0) {
      setError('Please select at least one phase');
      return;
    }

    setGenerating(true);
    setError(null);
    setGenerationComplete(false);

    try {
      const response = await axios.post(
        `${API}/reports/generate`,
        {
          project_id: project.id,
          report_format: reportFormat,
          phases: selectedPhases,
          include_charts: includeCharts,
          include_raw_data: includeRawData,
          report_title: reportTitle || undefined
        },
        
      );

      // Convert base64 to blob and download
      const byteCharacters = atob(response.data.file_content_base64);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: response.data.mime_type });
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = response.data.filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      setGenerationComplete(true);
      setTimeout(() => setGenerationComplete(false), 5000);
    } catch (error) {
      console.error('Error generating report:', error);
      setError(error.response?.data?.detail || 'Failed to generate report');
    } finally {
      setGenerating(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-12 h-12 text-blue-600 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-xl shadow-md p-6 border border-gray-100">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-3xl font-bold text-gray-900 mb-2">Report Generator</h2>
            <p className="text-gray-600">Generate comprehensive reports for your SAP analysis</p>
          </div>
          <FileText className="w-12 h-12 text-blue-600" />
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-start space-x-3">
          <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="font-semibold text-red-900">Error</h3>
            <p className="text-red-700 text-sm">{error}</p>
          </div>
        </div>
      )}

      {/* Success Display */}
      {generationComplete && (
        <div className="bg-green-50 border border-green-200 rounded-xl p-4 flex items-start space-x-3">
          <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="font-semibold text-green-900">Report Generated Successfully!</h3>
            <p className="text-green-700 text-sm">Your report has been downloaded.</p>
          </div>
        </div>
      )}

      {/* Configuration */}
      <div className="bg-white rounded-xl shadow-md p-6 border border-gray-100 space-y-6">
        {/* Report Title */}
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            Report Title (Optional)
          </label>
          <input
            type="text"
            value={reportTitle}
            onChange={(e) => setReportTitle(e.target.value)}
            placeholder="e.g., S/4HANA Readiness Assessment Q4 2024"
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>

        {/* Phase Selection */}
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-3">
            Select Phases to Include
          </label>
          {availablePhases.length === 0 ? (
            <div className="text-gray-500 text-center py-8">
              <AlertCircle className="w-12 h-12 mx-auto mb-3 text-gray-400" />
              <p>No analysis data available yet.</p>
              <p className="text-sm mt-1">Run analyses first to generate reports.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {availablePhases.map((phase) => (
                <div
                  key={phase.id}
                  onClick={() => togglePhase(phase.id)}
                  className="flex items-center justify-between p-4 border-2 rounded-lg cursor-pointer hover:bg-blue-50 transition-colors"
                  style={{
                    borderColor: selectedPhases.includes(phase.id) ? '#3b82f6' : '#e5e7eb',
                    backgroundColor: selectedPhases.includes(phase.id) ? '#eff6ff' : 'white'
                  }}
                >
                  <div className="flex items-center space-x-3">
                    {selectedPhases.includes(phase.id) ? (
                      <CheckSquare className="w-6 h-6 text-blue-600" />
                    ) : (
                      <Square className="w-6 h-6 text-gray-400" />
                    )}
                    <div>
                      <h4 className="font-semibold text-gray-900">{phase.name}</h4>
                      <p className="text-sm text-gray-600">{phase.modules} modules</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Report Format */}
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-3">
            Report Format
          </label>
          <div className="grid grid-cols-2 gap-4">
            <div
              onClick={() => setReportFormat('pdf')}
              className="flex items-center space-x-3 p-4 border-2 rounded-lg cursor-pointer hover:bg-red-50 transition-colors"
              style={{
                borderColor: reportFormat === 'pdf' ? '#ef4444' : '#e5e7eb',
                backgroundColor: reportFormat === 'pdf' ? '#fef2f2' : 'white'
              }}
            >
              <FileText className={`w-6 h-6 ${reportFormat === 'pdf' ? 'text-red-600' : 'text-gray-400'}`} />
              <div>
                <h4 className="font-semibold text-gray-900">PDF Report</h4>
                <p className="text-xs text-gray-600">Professional document</p>
              </div>
            </div>
            <div
              onClick={() => setReportFormat('excel')}
              className="flex items-center space-x-3 p-4 border-2 rounded-lg cursor-pointer hover:bg-green-50 transition-colors"
              style={{
                borderColor: reportFormat === 'excel' ? '#10b981' : '#e5e7eb',
                backgroundColor: reportFormat === 'excel' ? '#f0fdf4' : 'white'
              }}
            >
              <FileSpreadsheet className={`w-6 h-6 ${reportFormat === 'excel' ? 'text-green-600' : 'text-gray-400'}`} />
              <div>
                <h4 className="font-semibold text-gray-900">Excel Report</h4>
                <p className="text-xs text-gray-600">Data & charts</p>
              </div>
            </div>
          </div>
        </div>

        {/* Options */}
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-3">
            Report Options
          </label>
          <div className="space-y-3">
            <div
              onClick={() => setIncludeCharts(!includeCharts)}
              className="flex items-center justify-between p-3 border rounded-lg cursor-pointer hover:bg-gray-50"
            >
              <div className="flex items-center space-x-3">
                {includeCharts ? (
                  <CheckSquare className="w-5 h-5 text-blue-600" />
                ) : (
                  <Square className="w-5 h-5 text-gray-400" />
                )}
                <span className="text-gray-700">Include Charts & Visualizations</span>
              </div>
            </div>
            {reportFormat === 'excel' && (
              <div
                onClick={() => setIncludeRawData(!includeRawData)}
                className="flex items-center justify-between p-3 border rounded-lg cursor-pointer hover:bg-gray-50"
              >
                <div className="flex items-center space-x-3">
                  {includeRawData ? (
                    <CheckSquare className="w-5 h-5 text-blue-600" />
                  ) : (
                    <Square className="w-5 h-5 text-gray-400" />
                  )}
                  <span className="text-gray-700">Include Raw Data Tables</span>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Generate Button */}
        <button
          onClick={generateReport}
          disabled={generating || selectedPhases.length === 0}
          className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-6 py-4 rounded-xl hover:shadow-lg flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed font-semibold text-lg"
        >
          {generating ? (
            <>
              <Loader2 className="w-6 h-6 animate-spin" />
              <span>Generating Report...</span>
            </>
          ) : (
            <>
              <Download className="w-6 h-6" />
              <span>Generate & Download Report</span>
            </>
          )}
        </button>

        {selectedPhases.length === 0 && availablePhases.length > 0 && (
          <p className="text-sm text-center text-amber-600">
            Please select at least one phase to generate a report
          </p>
        )}
      </div>
    </div>
  );
};

export default ReportGenerator;
